import { Component, signal, WritableSignal } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogComponent } from 'src/app/controls/dialog/components/dialog.component';
import { SelectComponent } from 'src/app/controls/select/select.component';
import { CrudDialogBase } from 'src/app/controls/base/crud-dialog.base';
import { AnnualRegistration } from '../../models/annual-registration.interface';
import { AnnualRegistrationForm } from '../../models/annual-registration-form.interface';
import { SelectedItem } from 'src/app/controls/models/selected-item.interface';
import { AnnualRegistrationsTableService } from '../../services/annual-registrations-table.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'cec-annual-registration-dialog',
  templateUrl: './annual-registration.component.html',
  styleUrls: ['./annual-registration.component.scss'],
  standalone: true,
  imports: [DialogComponent, ReactiveFormsModule, SelectComponent]
})
export class AnnualRegistrationComponent extends CrudDialogBase<AnnualRegistrationForm, AnnualRegistration> {

  /** Season id reference */
  private seasonId: number | undefined = undefined;
  
  /** Annual create success message */
  protected successCreateKey = 'AnnualRegistrationCreateSuccess';

  /** Annual create fail message */
  protected failCreateKey = 'AnnualRegistrationCreateFailed';

  /** Annual update success message */
  protected successUpdateKey = 'AnnualRegistrationUpdateSuccess';

  /** Annual update fail message */
  protected failUpdateKey = 'AnnualRegistrationUpdateFailed';

  /** Season name reference */
  protected seasonName: WritableSignal<string> = signal('');

  /** Crew list reference */
  protected crews: SelectedItem[] = [];
  
  /** Competitor list reference  */
  protected competitors: SelectedItem[] = [];

  constructor(protected service: AnnualRegistrationsTableService) {
    super(service);

    /** builds a new annual registration form */
    this.formGroup = this.builder.group({
      crewId: [null as number | null, Validators.required],
      driverId: [null as number | null, Validators.required],
      navigatorId: [null as number | null, Validators.required]
    });

    /** enables/disables fields according to inputs */
    this.sub.add(
      this.formGroup.get('crewId').valueChanges.subscribe((value: number) => {
        if (!value) {
          this.disableFields();
          return;
        }

        /** gets the competitors per selected crew */
        this.sub.add(
          this.service.getCompetitors(value).subscribe((crew) => {
            this.competitors = crew?.competitors;
            this.formGroup.patchValue({
              driverId: crew.driverId,
              navigatorId: crew.navigatorId
            })
          })
        );
        
        /** enables the driver and navigator fields */
        this.enableFields();
      })
    );
  }

  /**
   * Loads the default values for the annual registration creation
   */
  protected override beforeDialogShow(): void {
    /** obtains list of available crews, season id and season name by one request */
    const seasonName$ = this.service.getActiveSeasonName();
    const seasonId$ = this.service.getActiveSeasonId();
    const crews$ = this.service.getAvailableCrews();

    /** executes the request */
    this.sub.add(
      forkJoin({
      seasonId: seasonId$,
      seasonName: seasonName$,
      crews: crews$,
      }).subscribe(({ seasonId, seasonName, crews }) => {
        this.seasonId = seasonId;
        this.seasonName.set(seasonName);
        this.crews = crews;
        this.disableFields();
      })
    );
  };

  /**
   * Disables driver and navigator fields
   */
  private enableFields(): void {
    this.formGroup.get('driverId').enable();
    this.formGroup.get('navigatorId').enable();
  }

  /**
   * Disables driver and navigator fields
   */
  private disableFields(): void {
    this.formGroup.get('driverId').disable();
    this.formGroup.get('navigatorId').disable();
  }

  /** @inheritdoc */
  protected afterDataSave() {
    /** reloads the annual registration list after data save */
    this.service.reload.next();

    /** navigates to the annual registered crews table */
    this.router.navigate(['/season', this.seasonId, this.seasonName(), 'registered-crews']);
  };

  /** @inheritdoc */
  protected afterDataPrefill() {}
}
